import axios from "axios";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import SearchDropdown from "../../../components/Dropdowns/SearchDropdown";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

import "react-datepicker/dist/react-datepicker.css";

const AddContractToClosedData = ({
  formData,
  disable,
  error,
  onSetFormData,
  onSetError,
  handleSubmit,
  handleClose,
}) => {
  const [config] = useAuth();
  const [propertyOptions, setPropertyOptions] = useState([]);
  const timeZone = "America/New_York";

  // Recalculate days when dates change (for Edit mode and bidirectional sync)
  useEffect(() => {
    const psaDate = formData.executed_purchase_agreeement;
    const ddDate = formData.due_diligence_expiration_date;
    const titleDate = formData.title;

    if (!psaDate) return;

    const base = moment.tz(psaDate, timeZone);

    const updates = {};

    // PSA-based days
    const dayCalculations = [
      { dateField: "earnest_deposite_confirmed", dayField: "earnest_deposite_confirmed_days" },
      { dateField: "due_diligence_delivery_receipt_confirmed", dayField: "due_diligence_delivery_receipt_confirmed_days" },
      { dateField: "title", dayField: "title_days" },
      { dateField: "due_diligence_expiration_date", dayField: "due_diligence_expiration_date_days" },
      { dateField: "financing_approval", dayField: "financing_approval_days" },
    ];

    dayCalculations.forEach(({ dateField, dayField }) => {
      if (formData[dateField]) {
        const target = moment.tz(formData[dateField], timeZone);
        const days = target.diff(base, "days");
        if (days >= 0 && formData[dayField] !== days) {
          updates[dayField] = days;
        }
      }
    });

    // Title → Address Title Issues
    if (titleDate && formData.address_title_issues) {
      const titleMoment = moment.tz(titleDate, timeZone);
      const issuesMoment = moment.tz(formData.address_title_issues, timeZone);
      const days = issuesMoment.diff(titleMoment, "days");
      if (days >= 0 && formData.address_title_issues_days !== days) {
        updates.address_title_issues_days = days;
      }
    }

    // Due Diligence Expiration → Scheduled Closing
    if (ddDate && formData.scheduled_closing) {
      const ddMoment = moment.tz(ddDate, timeZone);
      const closingMoment = moment.tz(formData.scheduled_closing, timeZone);
      const days = closingMoment.diff(ddMoment, "days");
      if (days >= 0 && formData.scheduled_closing_days !== days) {
        updates.scheduled_closing_days = days;
      }
    }

    if (Object.keys(updates).length > 0) {
      onSetFormData({ ...formData, ...updates });
    }
  }, [
    formData.executed_purchase_agreeement,
    formData.earnest_deposite_confirmed,
    formData.due_diligence_delivery_receipt_confirmed,
    formData.title,
    formData.due_diligence_expiration_date,
    formData.financing_approval,
    formData.address_title_issues,
    formData.scheduled_closing,
  ]);

  const handleChange = (value, name) => {
    onSetError({ ...error, [name]: "" });
    const newFormData = { ...formData, [name]: value };

    // 1. When changing PSA date → recalculate all dependent dates from days
    if (name === "executed_purchase_agreeement" && value) {
      const baseDate = moment.tz(value, timeZone);

      const fields = [
        { days: formData.earnest_deposite_confirmed_days, field: "earnest_deposite_confirmed" },
        { days: formData.due_diligence_delivery_receipt_confirmed_days, field: "due_diligence_delivery_receipt_confirmed" },
        { days: formData.title_days, field: "title" },
        { days: formData.due_diligence_expiration_date_days, field: "due_diligence_expiration_date" },
        { days: formData.financing_approval_days, field: "financing_approval" },
      ];

      fields.forEach(({ days, field }) => {
        if (days && !isNaN(parseInt(days))) {
          newFormData[field] = baseDate.clone().add(parseInt(days), "days").toDate();
        }
      });

      // Re-run dependent chains
      if (newFormData.title && formData.address_title_issues_days && !isNaN(parseInt(formData.address_title_issues_days))) {
        newFormData.address_title_issues = moment.tz(newFormData.title, timeZone).add(parseInt(formData.address_title_issues_days), "days").toDate();
      }
      if (newFormData.due_diligence_expiration_date && formData.scheduled_closing_days && !isNaN(parseInt(formData.scheduled_closing_days))) {
        newFormData.scheduled_closing = moment.tz(newFormData.due_diligence_expiration_date, timeZone).add(parseInt(formData.scheduled_closing_days), "days").toDate();
      }
    }

    // 2. When changing Title date
    else if (name === "title" && value && formData.address_title_issues_days) {
      const days = parseInt(formData.address_title_issues_days, 10);
      if (!isNaN(days)) {
        newFormData.address_title_issues = moment.tz(value, timeZone).add(days, "days").toDate();
      }
    }

    // 3. When changing Due Diligence Expiration date
    else if (name === "due_diligence_expiration_date" && value && formData.scheduled_closing_days) {
      const days = parseInt(formData.scheduled_closing_days, 10);
      if (!isNaN(days)) {
        newFormData.scheduled_closing = moment.tz(value, timeZone).add(days, "days").toDate();
      }
    }

    // 4. When changing any "_days" field → update corresponding date
    else if (["earnest_deposite_confirmed_days", "due_diligence_delivery_receipt_confirmed_days", "title_days", "due_diligence_expiration_date_days", "financing_approval_days"].includes(name)) {
      const days = value ? parseInt(value, 10) : null;
      const dateField = name.replace("_days", "");
      if (days !== null && formData.executed_purchase_agreeement) {
        newFormData[dateField] = moment.tz(formData.executed_purchase_agreeement, timeZone).add(days, "days").toDate();
      } else {
        newFormData[dateField] = null;
      }
    }

    // 5. Address Title Issues days
    else if (name === "address_title_issues_days") {
      const days = value ? parseInt(value, 10) : null;
      if (days !== null && formData.title) {
        newFormData.address_title_issues = moment.tz(formData.title, timeZone).add(days, "days").toDate();
      } else {
        newFormData.address_title_issues = null;
      }
    }

    // 6. Scheduled Closing days
    else if (name === "scheduled_closing_days") {
      const days = value ? parseInt(value, 10) : null;
      if (days !== null && formData.due_diligence_expiration_date) {
        newFormData.scheduled_closing = moment.tz(formData.due_diligence_expiration_date, timeZone).add(days, "days").toDate();
      } else {
        newFormData.scheduled_closing = null;
      }
    }

    onSetFormData(newFormData);
  };

  const fetchPropertySuggestions = (inputValue) => {
    axios.get(`${BASE_URL}/get-datalisting?list=executed-contract&search=${inputValue}`, config)
      .then(res => setPropertyOptions(res.data.data.properties.map(el => ({
        value: el.id, label: el?.property_name, desc: `${el.street_address}, ${el.city}, ${el.state}`
      }))))
      .catch(err => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message);
        }
      });
  };

  const timelineSteps = [
    { type: "date", name: "executed_loi", label: "Executed LOI" },
    { type: "date", name: "purchase_agreement_draft_distributed", label: "Purchase Agreement Draft Distributed" },
    { type: "date", name: "executed_purchase_agreeement", label: "Executed Purchase Agreement" },
    { type: "date", name: "transaction_timeline_distributed", label: "Transaction Timeline Distributed" },
    { type: "days", name: "earnest_deposite_confirmed_days", label: "Earnest Deposit Confirmed", dateField: "earnest_deposite_confirmed", base: "PSA" },
    { type: "days", name: "due_diligence_delivery_receipt_confirmed_days", label: "Due Diligence Delivery Confirmed", dateField: "due_diligence_delivery_receipt_confirmed", base: "PSA Executed" },
    { type: "days", name: "title_days", label: "Title", dateField: "title", base: "PSA" },
    { type: "days", name: "address_title_issues_days", label: "Address Title Issues", dateField: "address_title_issues", base: "Title" },
    { type: "date", name: "cordinate_inspection", label: "Coordinate Inspections" },
    { type: "date", name: "survey_received", label: "Survey Received" },
    { type: "date", name: "environmental_report_received", label: "Environmental Report Received" },
    { type: "date", name: "appraisal_received", label: "Appraisal Received" },
    { type: "days", name: "due_diligence_expiration_date_days", label: "Due Diligence Expiration Date", dateField: "due_diligence_expiration_date", base: "PSA" },
    { type: "date", name: "estoppel_snda_ordered", label: "Estoppel & SNDA Ordered" },
    { type: "days", name: "financing_approval_days", label: "Financing Approval", dateField: "financing_approval", base: "PSA" },
    { type: "date", name: "building_insurance_obtained", label: "Building Insurance Obtained" },
    { type: "date", name: "estoppel_snda_received", label: "Estoppel & SNDA Received" },
    { type: "date", name: "comission_invoice_wiring_instruction", label: "Commission Invoice & Wiring Instructions" },
    { type: "date", name: "review_settlement_statement", label: "Review Settlement Statement" },
    { type: "days", name: "scheduled_closing_days", label: "Scheduled Closing", dateField: "scheduled_closing", base: "Due Diligence Expiration Date" },
    { type: "date", name: "closed_date", label: "Closed (Final)" },
  ];

  return (
    <form onSubmit={handleSubmit} className="w-full bg-gray-50 min-h-screen p-6">
      <div className="max-w-7xl mx-auto overflow-x-auto max-h-[95vh]">

        {/* Property */}
        <div className="bg-white px-4 py-2 mb-4">
          <div className="flex items-center gap-6">
            <label className="dark-H head-4">Property</label>
            <div className="flex-1">
              <SearchDropdown
                placeholder="Search property..."
                text={formData?.property_name || ""}
                options={propertyOptions}
                fetchSuggestions={fetchPropertySuggestions}
                onSetOptions={setPropertyOptions}
                handleKeyDown={(e) => e.key === "Backspace" && onSetFormData({ ...formData, property_name: "", property_id: "" })}
                handleSelect={(option) => {
                  onSetError({ ...error, property_id: "" });
                  onSetFormData({ ...formData, property_name: option.label, property_id: option.value });
                  setPropertyOptions([]);
                }}
              />
              {error?.property_id && <p className="text-red-600 text-sm mt-1">{error.property_id}</p>}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white overflow-hidden">
          <div className="divide-y divide-gray-200">
            {timelineSteps.map((step, idx) => (
              <div key={idx} className="flex items-center gap-6 px-4 py-2 ">
                <div className="w-80 dark-H head-4">{step.label}</div>

                <div className="flex items-center gap-3 flex-1">
                  {step.type === "date" && (
                    <>
                      <DatePicker
                        className="body-N w-[40%]"
                        selected={formData[step.name]}
                        onChange={(date) => handleChange(date, step.name)}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="MM/DD/YYYY"
                      />
                      {error?.[step.name] && <span className="text-red-600 text-xs">{error[step.name]}</span>}
                    </>
                  )}

                  {step.type === "days" && (
                    <>
                      <input
                        type="number"
                        placeholder="Days"
                        value={formData[step.name] || ""}
                        onChange={(e) => handleChange(e.target.value, step.name)}
                        className="w-20 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-center focus:ring-2 focus:ring-green-500"
                      />
                      {error?.[step.name] && <span className="text-red-600 text-xs ml-2">{error[step.name]}</span>}
                      <span className="text-xs text-gray-500">days after {step.base || "PSA"}</span>
                      <DatePicker
                        selected={formData[step.dateField]}
                        onChange={(date) => handleChange(date, step.dateField)}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="MM/DD/YYYY"
                        className="body-N w-[40%]"
                      />
                      {error?.[step.dateField] && <span className="text-red-600 text-xs ml-2">{error[step.dateField]}</span>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-start gap-4 mt-8">
          <button
            type="submit"
            disabled={disable}
            className="save-button light-L head-5 green-bg-H"
          >
            {disable ? "Saving..." : "Save"}
          </button>
          <button type="button" onClick={handleClose} className="green-H ml-5">
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default AddContractToClosedData;